import { useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Send, Loader2, ImagePlus, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { DictaphoneTextarea } from "@/components/DictaphoneTextarea";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

const CATEGORIES = [
  { value: "bug", label: "Fehler / Bug", color: "#EF4444" },
  { value: "improvement", label: "Verbesserung", color: "#00E575" },
  { value: "question", label: "Frage", color: "#3B82F6" },
  { value: "praise", label: "Lob", color: "#A855F7" },
  { value: "other", label: "Sonstiges", color: "#71717A" },
];

const SEVERITIES = [
  { value: "low", label: "Niedrig" },
  { value: "medium", label: "Mittel" },
  { value: "high", label: "Hoch" },
];

export default function FeedbackPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const fileRef = useRef(null);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("bug");
  const [severity, setSeverity] = useState("medium");
  const [screenshot, setScreenshot] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fromPath = location.state?.fromPath || "";

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Bild zu groß (max. 8 MB)");
      return;
    }
    setScreenshot(f);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(f));
  };

  const clearShot = () => {
    setScreenshot(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async () => {
    if (!title.trim()) {
      toast.error("Bitte einen kurzen Titel angeben");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("title", title.trim());
      fd.append("description", description.trim());
      fd.append("category", category);
      fd.append("severity", severity);
      fd.append("page_url", fromPath || window.location.pathname);
      fd.append("user_agent", navigator.userAgent || "");
      if (screenshot) fd.append("screenshot", screenshot, screenshot.name || "shot.png");
      await api.post("/feedback", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Danke für dein Feedback!", {
        description: "Wir melden uns, sobald wir es bearbeitet haben.",
      });
      navigate(fromPath || "/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Senden fehlgeschlagen");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-100">
      <div className="max-w-2xl mx-auto px-4 py-6">
        <button
          data-testid="feedback-back-btn"
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm text-zinc-400 hover:text-zinc-200 mb-4"
        >
          <ArrowLeft size={16} /> Zurück
        </button>

        <h1 className="font-display text-2xl font-semibold mb-1">Feedback senden</h1>
        <p className="text-sm text-zinc-400 mb-6">
          Eingeloggt als <span className="text-zinc-200 font-medium">{user?.first_name} {user?.last_name}</span>. Browser-Info wird automatisch mitgeschickt.
        </p>

        <div className="space-y-5">
          {/* Category */}
          <div>
            <label className="block text-xs font-semibold text-zinc-400 mb-2 uppercase tracking-wider">
              Kategorie
            </label>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((c) => {
                const active = category === c.value;
                return (
                  <button
                    key={c.value}
                    data-testid={`feedback-category-${c.value}`}
                    onClick={() => setCategory(c.value)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium border transition ${
                      active
                        ? "border-[#00E575] bg-[#00E575]/10 text-[#00E575]"
                        : "border-zinc-700 bg-[#18181B] text-zinc-300 hover:border-zinc-600"
                    }`}
                  >
                    <span className="inline-block h-2 w-2 rounded-full mr-2" style={{ background: c.color }} />
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Severity */}
          <div>
            <label className="block text-xs font-semibold text-zinc-400 mb-2 uppercase tracking-wider">
              Dringlichkeit
            </label>
            <div className="flex gap-2">
              {SEVERITIES.map((s) => {
                const active = severity === s.value;
                return (
                  <button
                    key={s.value}
                    data-testid={`feedback-severity-${s.value}`}
                    onClick={() => setSeverity(s.value)}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition ${
                      active
                        ? "border-[#00E575] bg-[#00E575]/10 text-[#00E575]"
                        : "border-zinc-700 bg-[#18181B] text-zinc-300 hover:border-zinc-600"
                    }`}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-xs font-semibold text-zinc-400 mb-2 uppercase tracking-wider">
              Titel *
            </label>
            <input
              data-testid="feedback-title-input"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              placeholder="Kurz: Was ist passiert?"
              className="h-12 w-full bg-[#18181B] border border-zinc-700 rounded-xl px-4 text-zinc-50 focus:border-[#00E575] focus:ring-1 focus:ring-[#00E575] transition outline-none"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-semibold text-zinc-400 mb-2 uppercase tracking-wider">
              Beschreibung
            </label>
            <DictaphoneTextarea
              testid="feedback-description-input"
              value={description}
              onChange={(v) => setDescription((v || "").slice(0, 4000))}
              rows={6}
              placeholder="Tippen oder Mikrofon-Button drücken zum Diktieren – Was hast du gemacht? Was wäre erwartet gewesen? Welche Schritte führen zum Fehler?"
              className="bg-[#18181B] border-zinc-700 text-zinc-50 focus:border-[#00E575] focus:ring-[#00E575]"
            />
            <p className="mt-1 text-xs text-zinc-300 text-right">{description.length} / 4000</p>
          </div>

          {/* Screenshot */}
          <div>
            <label className="block text-xs font-semibold text-zinc-400 mb-2 uppercase tracking-wider">
              Screenshot (optional)
            </label>
            {previewUrl ? (
              <div className="relative rounded-xl overflow-hidden border border-zinc-700 bg-[#18181B]">
                <img src={previewUrl} alt="Screenshot Vorschau" className="w-full max-h-64 object-contain" />
                <button
                  data-testid="feedback-screenshot-clear-btn"
                  onClick={clearShot}
                  className="absolute top-2 right-2 h-9 w-9 rounded-full bg-[#09090B]/80 border border-zinc-700 text-zinc-200 hover:bg-red-500/20 hover:border-red-500 flex items-center justify-center"
                  aria-label="Screenshot entfernen"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <button
                data-testid="feedback-screenshot-add-btn"
                onClick={() => fileRef.current?.click()}
                className="h-14 w-full rounded-xl border-2 border-dashed border-zinc-700 hover:border-[#00E575]/50 text-zinc-400 hover:text-[#00E575] transition flex items-center justify-center gap-2 text-sm"
              >
                <ImagePlus size={18} /> Bild aus Galerie wählen
              </button>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={handleFile}
              className="hidden"
              data-testid="feedback-screenshot-input"
            />
          </div>

          {/* Submit */}
          <Button
            data-testid="feedback-submit-btn"
            onClick={submit}
            disabled={submitting || !title.trim()}
            className="h-14 w-full bg-[#00E575] text-[#09090B] font-semibold text-lg rounded-xl hover:bg-[#00C964] glow-green active:scale-[0.97] transition-all disabled:opacity-50"
          >
            {submitting ? <Loader2 className="animate-spin" /> : <><Send size={18} className="mr-2" /> Feedback senden</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
